// src/components/dashboards/ExportDialog.tsx
// Dynamic export dialog: checkboxes populated from backend registry.
// Exports as Excel (.xlsx) or JSON.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useExportRegistry from "hooks/useExportRegistry";
import { useEnvironmentFilter } from "hooks/useEnvironmentFilter";
import { Environment } from "types";
import { fetchData, createData } from "api/apiHelpers";
import api from "api/api";
import { useTranslation } from "../../i18n";
import logger from "utils/logger";
import {
  athenaDarkPaperSx,
  athenaBackdropSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaDarkActionsSx,
  athenaHeaderIconBoxSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
  athenaDarkDividerSx,
  athenaColors,
} from "../layout/AthenaDialogStyles";

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** If provided, exports only these patient IDs (from dashboard selection / NL query) */
  patientIds?: number[];
  /** Pre-selected environment IDs from the dashboard filter (user can override) */
  defaultEnvironmentIds?: number[];
}

interface IncludeTables {
  [key: string]: boolean;
}

// Async cohort-export job (backend builds the file server-side; the browser
// polls until it is ready and then downloads the finished file). This is what
// lets a 25k-patient cohort export at all: nothing large is ever held in the
// request thread or in browser memory.
interface ExportJob {
  state: "pending" | "running" | "done" | "error";
  current: number;
  total: number;
  download_ready: boolean;
  filename: string;
  format: string;
  error: string | null;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  patientIds,
  defaultEnvironmentIds,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { registry, loading: registryLoading } = useExportRegistry();
  const { allEnvironments, showFilter: hasMultipleEnvs } = useEnvironmentFilter();

  // Environment selection for export (defaults to dashboard filter or all)
  const [exportEnvIds, setExportEnvIds] = useState<Set<number>>(new Set());

  // Reset environment selection when dialog opens
  useEffect(() => {
    if (open) {
      if (defaultEnvironmentIds && defaultEnvironmentIds.length > 0) {
        setExportEnvIds(new Set(defaultEnvironmentIds));
      } else {
        setExportEnvIds(new Set(allEnvironments.map((e) => Number(e.id))));
      }
    }
  }, [open, defaultEnvironmentIds, allEnvironments]);

  const toggleEnv = (id: number) => {
    setExportEnvIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        // Don't allow empty selection
        if (next.size > 1) next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Batch select/deselect a group of real env ids at once (consortium master
  // checkbox). Mirrors toggleEnv's "never leave the selection fully empty"
  // invariant so the Download payload always resolves to a valid scope. The
  // caller only ever passes REAL env ids — a synthesized consortium header's
  // placeholder id is excluded upstream so it can't leak into the export scope.
  const setGroupSelection = (ids: (string | number)[], select: boolean) => {
    setExportEnvIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        const n = Number(id);
        if (Number.isNaN(n)) continue;
        if (select) next.add(n);
        else next.delete(n);
      }
      return next.size === 0 ? prev : next;
    });
  };

  const isEnvSelected = (envId: string | number) =>
    exportEnvIds.has(Number(envId));

  // Which consortium groups are expanded to reveal their child institutions.
  // Collapsed by default so a network with many centres stays compact; the
  // master checkbox on the header still batches every child at once.
  const [expandedConsortia, setExpandedConsortia] = useState<Set<string>>(
    new Set(),
  );
  const toggleConsortium = (key: string) =>
    setExpandedConsortia((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Selected tables
  const [includeTables, setIncludeTables] = useState<IncludeTables>({});
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(new Set());
  const [patientCount, setPatientCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [format, setFormat] = useState<"json" | "xlsx">("xlsx");

  // Monotonic token so an out-of-order count response from a superseded
  // selection (rapid env toggling) can never overwrite the latest count.
  const countReqSeq = React.useRef(0);

  const textPrimary = theme.palette.common.white;
  const textSecondary = alpha(theme.palette.common.white, 0.6);

  // Initialize defaults when registry loads
  useEffect(() => {
    if (registry) {
      const defaults: IncludeTables = {};
      registry.universal_tables.forEach((tbl) => {
        defaults[tbl.key] = true;
      });
      setIncludeTables(defaults);

      // Select all domains by default
      const domains = new Set(registry.domain_tables.map((d) => d.domain_key));
      setSelectedDomains(domains);
    }
  }, [registry]);

  // Fetch the count of patients that actually match the current export scope.
  // The cohort export selects patients by ENVIRONMENT (domains only choose which
  // tables are pulled), so the count must track the environment selection — this
  // is why unchecking a consortium now changes the number. When exporting a fixed
  // patient set (NL-query / dashboard selection) the count is just that set's size.
  const fetchCount = useCallback(async () => {
    if (patientIds && patientIds.length > 0) {
      setPatientCount(patientIds.length);
      return;
    }
    const seq = ++countReqSeq.current;
    setCountLoading(true);
    try {
      let url = "/insights/export-count/";
      // Only scope when a real subset is chosen; "all selected" means no filter
      // (backend defaults to the user's full accessible scope).
      if (
        hasMultipleEnvs &&
        exportEnvIds.size > 0 &&
        exportEnvIds.size < allEnvironments.length
      ) {
        url += `?environments=${Array.from(exportEnvIds).join(",")}`;
      }
      const resp = await fetchData<{ count: number }>(url);
      if (seq === countReqSeq.current) setPatientCount(resp.count);
    } catch {
      if (seq === countReqSeq.current) setPatientCount(null);
    } finally {
      if (seq === countReqSeq.current) setCountLoading(false);
    }
  }, [patientIds, hasMultipleEnvs, exportEnvIds, allEnvironments.length]);

  useEffect(() => {
    if (open) fetchCount();
  }, [open, fetchCount]);

  const toggleUniversal = (key: string) => {
    setIncludeTables((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDomain = (domainKey: string) => {
    setSelectedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domainKey)) next.delete(domainKey);
      else next.add(domainKey);
      return next;
    });
  };

  // Poll the async export job until the server has finished building the file.
  // Resolves with the terminal (done) job; throws on backend error / timeout.
  const pollExportJob = async (taskId: string): Promise<ExportJob> => {
    const startedAt = Date.now();
    const TIMEOUT_MS = 30 * 60 * 1000; // large cohorts can take minutes to build
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const job = await fetchData<ExportJob>(`/export/jobs/${taskId}/`);
      if (job.state === "error") {
        throw new Error(job.error || "Export failed while building the file.");
      }
      if (job.state === "done" && job.download_ready) return job;
      setExportProgress({ current: job.current ?? 0, total: job.total ?? 0 });
      if (Date.now() - startedAt > TIMEOUT_MS) {
        throw new Error("Export timed out.");
      }
      await sleep(1500);
    }
  };

  const handleExport = async () => {
    const domainKeys = Array.from(selectedDomains);
    if (domainKeys.length === 0) {
      logger.warn("No domains selected for export");
      return;
    }

    setExporting(true);
    setExportError(null);
    setExportProgress(null);
    try {
      const payload: Record<string, unknown> = {
        domain_keys: domainKeys,
        // Universal reshape tables ARE event tables now, so a checked table always
        // exports its rows; the legacy per-cancer event tables (retired) are the
        // only thing this flag ever gated, so it is always on.
        include_events: true,
        include_molecular: !!includeTables.molecularprofiletable,
        include_hematological: !!includeTables.hematologicalprofiletable,
        include_patient_profile: !!includeTables.patientprofiletable,
        include_trial_enrollment: !!includeTables.trialenrollmenttable,
        include_adverse_events: !!includeTables.adverseeventtable,
        include_medications: !!includeTables.medicationstatementtable,
        format: format,
      };

      if (patientIds && patientIds.length > 0) {
        payload.patient_ids = patientIds;
      }

      if (hasMultipleEnvs && exportEnvIds.size > 0 && exportEnvIds.size < allEnvironments.length) {
        payload.environment_ids = Array.from(exportEnvIds);
      }

      // 1. Enqueue the build on the server (returns immediately, never streams
      //    the payload through the request thread).
      const { task_id } = await createData<
        typeof payload,
        { task_id: string; patient_count: number }
      >("/export/cohort/", payload);

      // 2. Poll until the server has written the finished file.
      const job = await pollExportJob(task_id);

      // 3. Download the finished file (auth-gated blob → save locally).
      const resp = await api.get(`/export/jobs/${task_id}/download/`, {
        responseType: "blob",
      });
      const fallbackName = `cohort_export_${new Date()
        .toISOString()
        .slice(0, 10)}.${format === "xlsx" ? "xlsx" : "json"}`;
      const mime =
        format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/json";
      downloadBlob(resp.data as Blob, job.filename || fallbackName, mime);

      onClose();
    } catch (err) {
      logger.error("Export failed:", err);
      setExportError(
        err instanceof Error
          ? err.message
          : t("dashboard.export.error", "Export failed. Please try again."),
      );
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  // Consortium-grouped environment selector. Mirrors the grouping shipped in
  // EnvChipSelector: [XXMM]/consortium institution envs fold under their
  // consortium (via parent_consortium_env), rendered as a collapsible header
  // with a master (checked / unchecked / indeterminate) checkbox that batches
  // every child at once. Personal / ungrouped envs stay flat. When the
  // consortium env itself is not in the list (memberless consortium, invisible
  // to institution users) the header is SYNTHESIZED from the read-only
  // parent_consortium_env_name field; its placeholder id never enters the
  // export selection.
  const renderEnvironmentSelector = () => {
    const envs = allEnvironments;

    // Leading bracket tag fallback, e.g. "[XXMM] Althaia" -> "[XXMM]".
    const bracketTag = (name: string): string | null => {
      const m = name.match(/^\s*(\[[^\]]+\])/);
      return m ? m[1] : null;
    };

    // Real consortium envs the user can see, keyed by id.
    const consortiumById = new Map<string, Environment>();
    for (const e of envs) {
      if (e.kind === "consortium") consortiumById.set(String(e.id), e);
    }
    // Institution envs bucketed by the consortium id they point at.
    const childrenByParent = new Map<string, Environment[]>();
    for (const e of envs) {
      if (e.kind === "institution" && e.parent_consortium_env != null) {
        const pid = String(e.parent_consortium_env);
        const arr = childrenByParent.get(pid) ?? [];
        arr.push(e);
        childrenByParent.set(pid, arr);
      }
    }

    type Group = {
      consortium: Environment;
      institutions: Environment[];
      // True when `consortium` is a synthesized placeholder (the real env is
      // not in the user's list): its id must NOT enter the export selection.
      synthesized: boolean;
    };
    const groups: Group[] = [];
    const groupedIds = new Set<string>();

    // Every consortium id that is either visible or referenced by a visible
    // child, in the order it first appears (stable rendering).
    const consortiumOrder: string[] = [];
    const seenCid = new Set<string>();
    for (const e of envs) {
      const cid =
        e.kind === "consortium"
          ? String(e.id)
          : e.kind === "institution" && e.parent_consortium_env != null
            ? String(e.parent_consortium_env)
            : null;
      if (cid && !seenCid.has(cid)) {
        seenCid.add(cid);
        consortiumOrder.push(cid);
      }
    }

    for (const cid of consortiumOrder) {
      const institutions = childrenByParent.get(cid) ?? [];
      const real = consortiumById.get(cid);
      if (!real && institutions.length === 0) continue;
      let consortium: Environment;
      if (real) {
        consortium = real;
        groupedIds.add(String(real.id));
      } else {
        const name =
          institutions[0]?.parent_consortium_env_name ||
          bracketTag(institutions[0]?.name ?? "") ||
          t("dashboard.export.consortium", "Consortium");
        consortium = {
          id: cid,
          name,
          kind: "consortium",
          parent_consortium_env: null,
        } as Environment;
      }
      for (const inst of institutions) groupedIds.add(String(inst.id));
      groups.push({ consortium, institutions, synthesized: !real });
    }
    const standalone = envs.filter((e) => !groupedIds.has(String(e.id)));

    const renderEnvRow = (env: Environment, indent = false) => (
      <FormControlLabel
        key={env.id}
        control={
          <Checkbox
            checked={isEnvSelected(env.id)}
            onChange={() => toggleEnv(Number(env.id))}
            size="small"
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{ color: textPrimary, fontWeight: 500 }}
          >
            {env.name}
          </Typography>
        }
        sx={indent ? { ml: 3 } : undefined}
      />
    );

    const renderConsortiumGroup = (g: Group) => {
      const memberIds = g.institutions.map((i) => i.id);
      // A synthesized header is not a real env — never put its placeholder id in
      // the selection. A real consortium env is itself selectable alongside its
      // members (mirrors EnvChipSelector).
      const allInGroupIds = g.synthesized
        ? memberIds
        : [g.consortium.id, ...memberIds];
      const allSelected =
        allInGroupIds.length > 0 &&
        allInGroupIds.every((id) => isEnvSelected(id));
      const someSelected = allInGroupIds.some((id) => isEnvSelected(id));
      const selectAllCount = g.synthesized
        ? g.institutions.length
        : g.institutions.length + 1;
      const consortiumKey = String(g.consortium.id);
      const expanded = expandedConsortia.has(consortiumKey);
      const hasChildren = g.institutions.length > 0;
      const toggleAll = () => setGroupSelection(allInGroupIds, !allSelected);

      return (
        <Box key={`group-${g.consortium.id}`}>
          {/* Header: master select-all checkbox + collapse chevron. The
              checkbox batches every child (and, for a real consortium, the
              consortium env itself); the chevron only toggles child
              visibility. */}
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <FormControlLabel
              sx={{ flex: 1, minWidth: 0, mr: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={toggleAll}
                />
              }
              label={
                <Box
                  sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: textPrimary,
                      fontWeight: 700,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {g.consortium.name}
                  </Typography>
                  <Chip
                    label={t("dashboard.export.consortium", "Consortium")}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      ml: 0.75,
                      textTransform: "uppercase",
                      // Solid periwinkle on the dark dialog paper reads clearly;
                      // the previous 0.12-alpha primary tint was near-invisible.
                      bgcolor: athenaColors.periwinkle,
                      color: theme.palette.getContrastText(athenaColors.periwinkle),
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                  {hasChildren && (
                    <Typography
                      variant="caption"
                      sx={{ ml: 0.75, color: textSecondary }}
                    >
                      {t(
                        "dashboard.export.consortium_select_all",
                        "Select all ({count})",
                      ).replace("{count}", String(selectAllCount))}
                    </Typography>
                  )}
                </Box>
              }
            />
            {hasChildren && (
              <IconButton
                size="small"
                onClick={() => toggleConsortium(consortiumKey)}
                aria-label={
                  expanded
                    ? t(
                        "dashboard.export.consortium_collapse",
                        "Hide institutions",
                      )
                    : t(
                        "dashboard.export.consortium_expand",
                        "Show institutions",
                      )
                }
                sx={{ flexShrink: 0, color: textSecondary }}
              >
                {expanded ? (
                  <ExpandLessIcon sx={{ fontSize: 18 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            )}
          </Box>
          <Collapse in={expanded} unmountOnExit>
            {g.institutions.map((inst) => renderEnvRow(inst, true))}
          </Collapse>
        </Box>
      );
    };

    return (
      <FormGroup>
        {groups.map((g) => renderConsortiumGroup(g))}
        {standalone.map((env) => renderEnvRow(env))}
      </FormGroup>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: { sx: athenaBackdropSx },
      }}
      PaperProps={{ sx: athenaDarkPaperSx }}
    >
      <DialogTitle sx={athenaDarkTitleSx}>
        <Box sx={athenaHeaderIconBoxSx}>
          <FileDownloadOutlinedIcon sx={{ color: athenaColors.periwinkle, fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {t("dashboard.export.title", "Export Patient Data")}
          </Typography>
          {patientIds && patientIds.length > 0 && (
            <Typography variant="body2" sx={{ mt: 0.25, color: athenaColors.periwinkle }}>
              {patientIds.length} patients selected
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={athenaDarkContentSx}>
        {registryLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Universal Profiles */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ mb: 1, color: textPrimary, fontSize: "0.85rem" }}
              >
                {t("dashboard.export.universal_profiles", "Universal Profiles")}
              </Typography>
              <FormGroup>
                {registry?.universal_tables.map((table) => (
                  <FormControlLabel
                    key={table.key}
                    control={
                      <Checkbox
                        checked={!!includeTables[table.key]}
                        onChange={() => toggleUniversal(table.key)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500 }}>
                        {table.label}
                      </Typography>
                    }
                  />
                ))}
              </FormGroup>
            </Box>

            <Divider sx={athenaDarkDividerSx} />

            {/* Cancer Domains */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ mb: 1, color: textPrimary, fontSize: "0.85rem" }}
              >
                {t("dashboard.export.cancer_domains", "Cancer Domains")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {registry?.domain_tables.map((domain) => (
                  <Chip
                    key={domain.domain_key}
                    label={domain.label}
                    onClick={() => toggleDomain(domain.domain_key)}
                    color={selectedDomains.has(domain.domain_key) ? "primary" : "default"}
                    variant={selectedDomains.has(domain.domain_key) ? "filled" : "outlined"}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      ...(!selectedDomains.has(domain.domain_key) && {
                        color: textSecondary,
                        borderColor: alpha(athenaColors.periwinkle, 0.35),
                      }),
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Environment scope — only shown when user has 2+ environments */}
            {hasMultipleEnvs && (
              <>
                <Divider sx={athenaDarkDividerSx} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1, color: textPrimary, fontSize: "0.85rem" }}
                  >
                    {t("dashboard.export.environments", "Environments")}
                  </Typography>
                  {renderEnvironmentSelector()}
                </Box>
              </>
            )}

            <Divider sx={athenaDarkDividerSx} />

            {/* Format */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: textPrimary, fontSize: "0.85rem" }}
              >
                {t("dashboard.export.format", "Format")}:
              </Typography>
              <Chip
                label="Excel (.xlsx)"
                onClick={() => setFormat("xlsx")}
                color={format === "xlsx" ? "primary" : "default"}
                variant={format === "xlsx" ? "filled" : "outlined"}
                size="small"
                sx={{
                  fontWeight: 600,
                  ...( format !== "xlsx" && { color: textSecondary, borderColor: alpha(athenaColors.periwinkle, 0.35) }),
                }}
              />
              <Chip
                label="JSON"
                onClick={() => setFormat("json")}
                color={format === "json" ? "primary" : "default"}
                variant={format === "json" ? "filled" : "outlined"}
                size="small"
                sx={{
                  fontWeight: 600,
                  ...( format !== "json" && { color: textSecondary, borderColor: alpha(athenaColors.periwinkle, 0.35) }),
                }}
              />
            </Box>

            {/* Multi-sheet info */}
            {format === "xlsx" && (
              <Alert severity="info" sx={{ py: 0 }}>
                <Typography variant="caption">
                  {t(
                    "dashboard.export.multi_sheet_info",
                    "The Excel file will contain a separate sheet for each selected table (profiles, cancer summaries, events, etc.), linked by a patient ID column."
                  )}
                </Typography>
              </Alert>
            )}

            {/* Patient Count */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: alpha(athenaColors.periwinkle, 0.15),
                border: `1px solid ${alpha(athenaColors.periwinkle, 0.35)}`,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: textSecondary, fontWeight: 500 }}>
                {t("dashboard.export.matching_patients", "Matching patients")}
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, color: textPrimary }}>
                {countLoading ? "..." : patientCount ?? "-"}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      {/* Pinned status region: rendered OUTSIDE the scrollable content, directly
          above the footer, so the "preparing" bar / error is visible the instant
          it appears no matter where the user has scrolled. */}
      {(exporting || exportError) && (
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${alpha(athenaColors.periwinkle, 0.2)}`,
          }}
        >
          {exporting && (
            <>
              <LinearProgress
                variant={
                  exportProgress && exportProgress.total > 0
                    ? "determinate"
                    : "indeterminate"
                }
                value={
                  exportProgress && exportProgress.total > 0
                    ? Math.min(
                        100,
                        (exportProgress.current / exportProgress.total) * 100,
                      )
                    : undefined
                }
                sx={{ borderRadius: 1, height: 6 }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.75,
                  textAlign: "center",
                  color: textPrimary,
                  fontWeight: 500,
                }}
              >
                {exportProgress && exportProgress.total > 0
                  ? t(
                      "dashboard.export.preparing_progress",
                      "Preparing your export… {current} / {total} patients",
                    )
                      .replace("{current}", String(exportProgress.current))
                      .replace("{total}", String(exportProgress.total))
                  : t("dashboard.export.preparing", "Preparing your export…")}
              </Typography>
            </>
          )}
          {exportError && (
            <Alert severity="error" sx={{ py: 0.5, mt: exporting ? 1 : 0 }}>
              <Typography variant="caption">{exportError}</Typography>
            </Alert>
          )}
        </Box>
      )}

      <DialogActions sx={athenaDarkActionsSx}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={athenaDarkSecondaryButtonSx}
        >
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={exporting || selectedDomains.size === 0}
          sx={athenaDarkPrimaryButtonSx}
        >
          {exporting
            ? t("dashboard.export.exporting", "Exporting...")
            : t("dashboard.export.download", "Download")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Helper: download a Blob
// ---------------------------------------------------------------------------
function downloadBlob(blob: Blob, filename: string, _mimeType: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default ExportDialog;
